import { toast } from 'vue-sonner';
import { deleteToken, getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging';

const PUSH_SCOPE = '/firebase-cloud-messaging-push-scope';
const PUSH_SW_PATH = '/firebase-messaging-sw.js';

type PushPermissionState = NotificationPermission | 'unsupported';
const PUSH_REQUEST_TIMEOUT_MS = 15000;
type PushRequestPhase = 'idle' | 'permission' | 'registering';

function browserPermission(): PushPermissionState {
    if (import.meta.server) return 'default';
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
}

export function usePushNotifications() {
    const supported = useState<boolean>('push-supported', () => false);
    const initialized = useState<boolean>('push-initialized', () => false);
    const busy = useState<boolean>('push-busy', () => false);
    const phase = useState<PushRequestPhase>('push-phase', () => 'idle');
    const permission = useState<PushPermissionState>('push-permission', () => browserPermission());
    const currentToken = useState<string>('push-token', () => '');
    const foregroundListenerReady = useState<boolean>('push-foreground-listener-ready', () => false);

    let messagingPromise: Promise<Messaging | null> | null = null;
    let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

    async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        try {
            return await Promise.race([
                promise,
                new Promise<T>((_, reject) => {
                    timeoutId = setTimeout(() => {
                        reject(new Error(message));
                    }, timeoutMs);
                }),
            ]);
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    async function resolveMessaging(): Promise<Messaging | null> {
        if (import.meta.server) return null;
        if (messagingPromise) return messagingPromise;

        messagingPromise = (async () => {
            const nuxtApp = useNuxtApp();
            if (!nuxtApp.$firebaseApp) {
                return null;
            }

            const supportedHere = await isSupported().catch(() => false);
            supported.value = supportedHere;
            if (!supportedHere) {
                permission.value = 'unsupported';
                return null;
            }

            const messaging = getMessaging(nuxtApp.$firebaseApp);
            bindForegroundListener(messaging);
            return messaging;
        })();

        return messagingPromise;
    }

    async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
        if (import.meta.server || !('serviceWorker' in navigator)) return null;
        if (swRegistrationPromise) return swRegistrationPromise;

        swRegistrationPromise = navigator.serviceWorker.register(PUSH_SW_PATH, {
            scope: PUSH_SCOPE,
        }).catch((error) => {
            console.warn('Push service worker registration failed:', error);
            return null;
        });

        return swRegistrationPromise;
    }

    async function showForegroundNotification(payload: any): Promise<void> {
        if (import.meta.server) return;

        const notification = payload?.notification || {};
        const data = payload?.data || {};
        const title = String(notification.title || data.title || 'Vuey Studio').trim();
        const body = String(notification.body || data.body || '').trim();
        const icon = String(notification.icon || '/brand/appicon.png').trim() || '/brand/appicon.png';
        const badge = String(notification.badge || '/brand/appicon.png').trim() || '/brand/appicon.png';
        const targetUrl = String(data.actionPath || data.link || '/').trim() || '/';
        const registration = await ensureServiceWorker();

        if (registration && permission.value === 'granted') {
            await registration.showNotification(title, {
                body,
                icon,
                badge,
                data: {
                    targetUrl,
                },
            });
            return;
        }

        toast.message(title, {
            description: body,
        });
    }

    function bindForegroundListener(messaging: Messaging | null) {
        if (!messaging || foregroundListenerReady.value) {
            return;
        }

        onMessage(messaging, (payload) => {
            console.info('[push] Foreground message received', payload);
            showForegroundNotification(payload).catch((error) => {
                console.warn('Unable to show foreground push notification:', error);
            });
        });

        foregroundListenerReady.value = true;
    }

    async function persistToken(token: string): Promise<void> {
        const { $auth } = useNuxtApp();
        const user = $auth.currentUser;
        if (!user?.uid || !token) {
            return;
        }

        await $fetch('/api/notifications/devices', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${await user.getIdToken()}`,
            },
            body: {
                token,
                permission: permission.value,
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                platform: typeof navigator !== 'undefined' ? navigator.platform : '',
            },
        });

        console.info('[push] Device token saved', {
            userId: user.uid,
            tokenPreview: token.slice(0, 12),
        });
    }

    async function removeToken(token: string): Promise<void> {
        const { $auth } = useNuxtApp();
        const user = $auth.currentUser;
        if (!user?.uid || !token) {
            return;
        }

        await $fetch('/api/notifications/devices', {
            method: 'DELETE',
            headers: {
                authorization: `Bearer ${await user.getIdToken()}`,
            },
            body: {
                token,
            },
        });
    }

    async function syncPushToken(): Promise<string | null> {
        if (import.meta.server) return null;

        permission.value = browserPermission();
        if (permission.value !== 'granted') {
            return null;
        }

        const config = useRuntimeConfig();
        const vapidKey = String(config.public.firebaseVapidKey || '').trim();
        if (!vapidKey) {
            console.warn('Firebase VAPID key is missing. Push registration skipped.');
            return null;
        }

        const messaging = await resolveMessaging();
        const registration = await ensureServiceWorker();

        if (!messaging || !registration) {
            return null;
        }

        const token = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: registration,
        }).catch((error) => {
            console.warn('Unable to get push token:', error);
            return '';
        });

        if (!token) {
            return null;
        }

        currentToken.value = token;
        await persistToken(token);
        return token;
    }

    async function requestPushPermission(): Promise<boolean> {
        if (import.meta.server) return false;
        if (!('Notification' in window)) {
            permission.value = 'unsupported';
            return false;
        }
        if (!window.isSecureContext) {
            toast.error('Push notifications need a secure browser context.');
            return false;
        }

        try {
            phase.value = 'permission';
            const next = await withTimeout(
                Notification.requestPermission(),
                PUSH_REQUEST_TIMEOUT_MS,
                'The browser did not finish the notification permission request in time.',
            );
            permission.value = next;

            if (next !== 'granted') {
                toast.message('Push notifications stayed off.');
                return false;
            }

            busy.value = true;
            phase.value = 'registering';
            const token = await withTimeout(
                syncPushToken(),
                PUSH_REQUEST_TIMEOUT_MS,
                'Vuey could not finish registering this browser for push notifications.',
            );

            if (!token) {
                toast.error('Permission was granted, but this browser could not be registered for push yet.');
                return false;
            }

            toast.success('Push notifications enabled.');
            return true;
        } catch (error: any) {
            console.warn('Unable to enable push notifications:', error);
            toast.error(error?.message || 'Push notifications could not be enabled right now.');
            return false;
        } finally {
            busy.value = false;
            phase.value = 'idle';
        }
    }

    async function disablePush(): Promise<void> {
        if (import.meta.server) return;

        busy.value = true;
        try {
            const messaging = await resolveMessaging();
            const token = currentToken.value;

            if (messaging && token) {
                await deleteToken(messaging).catch(() => null);
            }

            if (token) {
                await removeToken(token).catch((error) => {
                    console.warn('Unable to remove push token registration:', error);
                });
            }

            currentToken.value = '';
            toast.success('Push notifications disabled on this device.');
        } finally {
            busy.value = false;
        }
    }

    async function initPushNotifications(): Promise<void> {
        if (import.meta.server || initialized.value) {
            return;
        }

        initialized.value = true;
        permission.value = browserPermission();
        await resolveMessaging();

        if (permission.value === 'granted') {
            await syncPushToken();
        }
    }

    return {
        supported,
        initialized,
        busy,
        phase,
        permission,
        currentToken,
        initPushNotifications,
        syncPushToken,
        requestPushPermission,
        disablePush,
    };
}
